/**
 * Chat service
 * Handles API requests for chat management
 */

import { api } from '../utils/api-client'
import type { GetChatHistoryResponse } from '@taqat/core'
import type { CompactionEvent } from '@taqat/ai'

/**
 * Query parameters for chat history requests
 */
export interface ChatQueryParams {
  page?: number
  limit?: number
}

/** Visual flavour of a follow-up chip. */
export type FollowupKind = 'action' | 'exploration'

/**
 * A single AI-generated follow-up suggestion chip for the Super-Agent chat.
 */
export interface FollowupSuggestion {
  /** Short chip text. */
  label: string
  /** Whether the chip performs an action or opens an exploration. */
  kind: FollowupKind
  /** kebab-case lucide-react icon name, picked per-suggestion by the model. */
  icon: string
  /** Full prompt dropped into the composer on click. */
  fullPrompt: string
}

/**
 * Chat service class
 */
export class ChatService {
  /**
   * Fetches chat history for a specific agent
   * @param agentId - The unique identifier of the agent
   * @param params - Query parameters for filtering and pagination
   * @returns Promise resolving to agent chat history response
   */
  static async getAgentChatHistory(agentId: string, params?: ChatQueryParams): Promise<GetChatHistoryResponse> {
    const searchParams = new URLSearchParams()

    if (params?.page) {
      searchParams.append('page', params.page.toString())
    }

    if (params?.limit) {
      searchParams.append('limit', params.limit.toString())
    }

    const queryString = searchParams.toString()
    const url = queryString ? `/chat/agent/${agentId}?${queryString}` : `/chat/agent/${agentId}`

    return api.get(url, {
      requiresAuth: true,
      showErrorToast: true,
      showSuccessToast: false,
    })
  }

  /**
   * Fetches chat history for a channel user (web-chat user)
   * Uses channel user authentication endpoint
   * @param agentId - The unique identifier of the agent
   * @param params - Query parameters for filtering and pagination
   * @returns Promise resolving to agent chat history response
   */
  static async getChannelUserChatHistory(agentId: string, params?: ChatQueryParams): Promise<GetChatHistoryResponse> {
    const searchParams = new URLSearchParams()

    if (params?.page) {
      searchParams.append('page', params.page.toString())
    }

    if (params?.limit) {
      searchParams.append('limit', params.limit.toString())
    }

    const queryString = searchParams.toString()
    const url = queryString ? `/channel-user-chat/agent/${agentId}?${queryString}` : `/channel-user-chat/agent/${agentId}`

    return api.get(url, {
      requiresAuth: true,
      showErrorToast: false, // Don't show toast for channel users - handle gracefully
      showSuccessToast: false,
    })
  }

  /**
   * Fetches chat history for the current user and workspace
   * @param params - Query parameters for filtering and pagination
   * @returns Promise resolving to chat history response
   */
  static async getChatHistory(params?: ChatQueryParams): Promise<GetChatHistoryResponse> {
    const searchParams = new URLSearchParams()
    
    if (params?.page) {
      searchParams.append('page', params.page.toString())
    }
    
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString())
    }
    
    const queryString = searchParams.toString()
    const url = queryString ? `/chat?${queryString}` : '/chat'
    
    return api.get(url, {
      requiresAuth: true,
      showErrorToast: true,
      showSuccessToast: false,
    }) 
  }

  /**
   * Fetches messages for a specific chat session
   * @param chatId - The unique identifier of the chat session
   * @param params - Query parameters for filtering and pagination
   * @returns Promise resolving to chat messages response
   */
  static async getChatMessages(chatId: string, params?: ChatQueryParams): Promise<any> {
    const searchParams = new URLSearchParams()
    
    if (params?.page) {
      searchParams.append('page', params.page.toString())
    }
    
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString())
    }
    
    const queryString = searchParams.toString()
    const url = queryString ? `/chat/${chatId}/messages?${queryString}` : `/chat/${chatId}/messages`
    
    return api.get(url, {
      requiresAuth: true,
      // Pages render their own error UI; suppress the api-client toast so a
      // race between a chat deletion and an in-flight messages fetch doesn't
      // surface a stray "chat not found" toast.
      showErrorToast: false,
      showSuccessToast: false,
    })
  }

  /**
   * Fetches details for a specific chat
   * @param chatId - The unique identifier of the chat
   * @returns Promise resolving to chat details response
   */
  static async getChatById(chatId: string): Promise<{ success: boolean; data: any; message: string }> {
    return api.get(`/chat/${chatId}`, {
      requiresAuth: true,
      // Pages render their own "Chat not found" UI via setError; the badge
      // (chat-credits-badge) handles 404 silently. Suppress the redundant
      // api-client toast so a deletion race doesn't pop a stray toast.
      showErrorToast: false,
      showSuccessToast: false,
    })
  }

  /**
   * Fetches details for a specific team chat
   * @param teamId - The unique identifier of the team
   * @param chatId - The unique identifier of the chat
   * @returns Promise resolving to chat details response
   */
  static async getTeamChatById(teamId: string, chatId: string): Promise<{ success: boolean; data: any; message: string }> {
    return api.get(`/teams/${teamId}/chats/${chatId}`, {
      requiresAuth: true,
      showErrorToast: true,
      showSuccessToast: false,
    })
  }

  /**
   * Fetches messages for a specific team chat
   * @param teamId - The unique identifier of the team
   * @param chatId - The unique identifier of the chat
   * @returns Promise resolving to messages response
   */
  static async getTeamChatMessages(teamId: string, chatId: string): Promise<{ success: boolean; data: any }> {
    return api.get(`/teams/${teamId}/chats/${chatId}/messages`, {
      requiresAuth: true,
      showErrorToast: false,
      showSuccessToast: false,
    })
  }

  /**
   * Deletes a chat and all its associated messages
   * @param chatId - The ID of the chat to delete
   * @returns Promise resolving to deletion response
   */
  static async deleteChat(chatId: string): Promise<{ success: boolean; message: string }> {
    return api.delete(`/chat/${chatId}`, {
      requiresAuth: true,
      showErrorToast: true,
      // Callers (DeleteChatDialog) own the success toast so we can show
      // "Chat deleted" instead of the generic "Operation completed".
      showSuccessToast: false,
    })
  }

  /**
   * Updates the title for a regular or agent chat
   * Persists manual chat renames from the chat header
   * @param chatId - The unique identifier of the chat to rename
   * @param title - The new chat title to persist
   * @returns Promise resolving to the updated chat response
   */
  static async updateChatTitle(
    chatId: string,
    title: string
  ): Promise<{ success: boolean; data: any; message: string }> {
    return api.patch(`/chat/${chatId}`, { title }, {
      requiresAuth: true,
      showErrorToast: true,
      showSuccessToast: false,
    })
  }

  /**
   * Creates a public share link for a chat
   * Allows sharing chat conversations with anyone via a public URL
   * @param chatId - The unique identifier of the chat to share
   * @returns Promise resolving to share record with ID
   */
  static async createChatShare(chatId: string): Promise<{ success: boolean; data: any }> {
    return api.post(`/chat/${chatId}/share`, {}, {
      requiresAuth: true,
      showErrorToast: true,
      showSuccessToast: true,
    })
  }

  /**
   * Retrieves shared chat details using share link ID
   * Public endpoint - no authentication required
   * @param shareId - The unique identifier of the share link
   * @returns Promise resolving to shared chat details with agent info
   */
  static async getSharedChat(shareId: string): Promise<{ success: boolean; data: any }> {
    return api.get(`/shared/${shareId}`, {
      requiresAuth: false,
      showErrorToast: true,
      showSuccessToast: false,
    })
  }

  /**
   * Retrieves messages for a shared chat
   * Public endpoint - no authentication required
   * @param shareId - The unique identifier of the share link
   * @returns Promise resolving to array of chat messages
   */
  static async getSharedChatMessages(shareId: string): Promise<{ success: boolean; data: any[] }> {
    return api.get(`/shared/${shareId}/messages`, {
      requiresAuth: false,
      showErrorToast: true,
      showSuccessToast: false,
    })
  }

  /**
   * Revokes a chat share link
   * Deletes the public share link making it inaccessible
   * @param chatId - The unique identifier of the chat
   * @returns Promise resolving to success message
   */
  static async deleteChatShare(chatId: string): Promise<{ success: boolean; message: string }> {
    return api.delete(`/chat/${chatId}/share`, {
      requiresAuth: true,
      showErrorToast: true,
      showSuccessToast: true,
    })
  }

  /**
   * Requests AI-generated follow-up suggestion chips for a Super-Agent chat.
   * The server applies all scoping/suppression rules and returns an empty
   * list when chips should not be shown — callers just render the result.
   *
   * @param chatId - The chat to generate suggestions for
   * @param locale - Locked embed session locale (e.g. "en"/"ar"); when set the
   *   server authors the chips only in that language. Omitted in the main app.
   * @returns Promise resolving to the suggestion list (possibly empty)
   */
  static async generateFollowups(
    chatId: string,
    locale?: string | null
  ): Promise<{ success: boolean; data: { suggestions: FollowupSuggestion[] } }> {
    return api.post(`/chat/${chatId}/followups`, locale ? { locale } : {}, {
      requiresAuth: true,
      showErrorToast: false,
      showSuccessToast: false,
    })
  }

  /**
   * Compacts a chat on demand — backs the `/compact` slash command.
   * Summarizes older turns server-side and returns the resulting compaction
   * event (or `null` when the conversation is too short to compact).
   * Toasts are handled by the caller so it can distinguish the no-op case.
   *
   * @param chatId - The chat to compact
   * @returns Promise resolving to the compaction event, possibly null
   */
  static async compactChat(
    chatId: string
  ): Promise<{ success: boolean; data: { compaction: CompactionEvent | null } }> {
    return api.post(`/chat/${chatId}/compact`, {}, {
      requiresAuth: true,
      showErrorToast: false,
      showSuccessToast: false,
    })
  }

  /**
   * Clears the Super-Agent hand-off on a routed chat — flips the chat row's
   * agent back to the supervisor so future turns route through SuperAgent.
   * The `cameFromSuperAgent` flag stays so the user can pick another
   * specialist later. Backs the × on the "Talking to: <Agent>" chip.
   *
   * @param chatId - Chat to revert to the supervisor
   */
  static async clearChatHandoff(
    chatId: string
  ): Promise<{ success: boolean; data?: any; message?: string }> {
    return api.post(`/chat/${chatId}/clear-handoff`, {}, {
      requiresAuth: true,
      showErrorToast: true,
      showSuccessToast: false,
    })
  }

}

/**
 * Convenience instance of the chat service
 */
export const chatService = {
  getChatHistory: ChatService.getChatHistory,
  getChatMessages: ChatService.getChatMessages,
  getChatById: ChatService.getChatById,
  getTeamChatById: ChatService.getTeamChatById,
  getTeamChatMessages: ChatService.getTeamChatMessages,
  deleteChat: ChatService.deleteChat,
  updateChatTitle: ChatService.updateChatTitle,
  getAgentChatHistory: ChatService.getAgentChatHistory,
  createChatShare: ChatService.createChatShare,
  getSharedChat: ChatService.getSharedChat,
  getSharedChatMessages: ChatService.getSharedChatMessages,
  deleteChatShare: ChatService.deleteChatShare,
  generateFollowups: ChatService.generateFollowups,
  compactChat: ChatService.compactChat,
  clearChatHandoff: ChatService.clearChatHandoff,
}

/**
 * Export default for easier imports
 */
